import { addDays, format } from 'date-fns'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { DateInput } from '@/components/ui/DateInput'
import { DialogFooter } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { TimePicker, type TimeValue } from '@/components/ui/time-picker'
import {
  type OutboundCallAutomation,
  useClearAutomationFollowUpReminder,
  useSetAutomationFollowUpReminder,
} from '@/models/outboundCallAutomation'

// Reminders are meant to land in the morning digest, so a fresh one defaults to
// 9am tomorrow.
const DEFAULT_TIME: TimeValue = { hours: 9, minutes: 0 }

function combineDateTime(date: Date, time: TimeValue): Date {
  const combined = new Date(date)
  combined.setHours(time.hours, time.minutes, 0, 0)
  return combined
}

interface AutomationFollowUpReminderFormProps {
  run: OutboundCallAutomation
  onDone: () => void
}

/**
 * Schedules a reminder to run a follow-up of a past automation (INS-290). At the
 * scheduled time the rep gets the reminder email + in-app toast, whose CTA reopens
 * this dialog composed as a follow-up of `run` — one click from "Save and start".
 */
export function AutomationFollowUpReminderForm({ run, onDone }: AutomationFollowUpReminderFormProps) {
  const setMutation = useSetAutomationFollowUpReminder()
  const clearMutation = useClearAutomationFollowUpReminder()

  const existing = run.followUpReminder ? new Date(run.followUpReminder.reminderDate) : undefined
  const [date, setDate] = useState<Date | undefined>(existing ?? addDays(new Date(), 1))
  const [time, setTime] = useState<TimeValue>(
    existing ? { hours: existing.getHours(), minutes: existing.getMinutes() } : DEFAULT_TIME
  )
  const [note, setNote] = useState(run.followUpReminder?.message ?? '')

  const save = () => {
    if (!date) return
    setMutation.mutate(
      { id: run.id, reminderDate: combineDateTime(date, time).toISOString(), note },
      {
        onSuccess: () => {
          toast.success('Follow-up reminder scheduled')
          onDone()
        },
        onError: () => toast.error('Failed to schedule the reminder'),
      }
    )
  }

  const remove = () => {
    clearMutation.mutate(run.id, {
      onSuccess: () => {
        toast.success('Follow-up reminder removed')
        onDone()
      },
      onError: () => toast.error('Failed to remove the reminder'),
    })
  }

  return (
    <div className="flex flex-col gap-4">
      <Label className="block">
        Remind me to follow up on “{run.name || 'Automation'}” — {run.followUpEligibleCount} lead
        {run.followUpEligibleCount === 1 ? '' : 's'} still in Called status
      </Label>
      {run.followUpReminder && (
        <p className="text-muted-foreground text-sm">
          Currently scheduled for {format(new Date(run.followUpReminder.reminderDate), 'MMM d, h:mm a')}.
        </p>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label>Date</Label>
          <DateInput value={date} onChange={setDate} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Time</Label>
          <TimePicker value={time} onChange={setTime} />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="automation-reminder-note">Note</Label>
        <Textarea
          id="automation-reminder-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note (defaults to “Run a follow-up of {name}”)"
        />
      </div>
      <DialogFooter className="justify-between sm:justify-between">
        {run.followUpReminder ? (
          <Button
            variant="outline"
            onClick={remove}
            loading={clearMutation.isPending ? 'Removing…' : undefined}
          >
            Remove reminder
          </Button>
        ) : (
          <span />
        )}
        <span className="flex items-center gap-2">
          <Button variant="outline" onClick={onDone} disabled={setMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!date} loading={setMutation.isPending ? 'Scheduling…' : undefined}>
            Schedule reminder
          </Button>
        </span>
      </DialogFooter>
    </div>
  )
}
